import { tenantsApi } from '@/api/tenants'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { useInviteTenantUser } from '@/hooks'
import type { TenantMemberRole } from '@/types'
import { useQuery } from '@tanstack/react-query'
import { Check } from 'lucide-react'
import { useEffect, useState } from 'react'
import { Controller, useForm } from 'react-hook-form'

interface InviteUserDialogProps {
  defaultTenantId: string
  open: boolean
  onOpenChange: (open: boolean) => void
}

interface FormValues {
  tenantId: string
  email: string
  role: TenantMemberRole
}

export function InviteUserDialog({ defaultTenantId, open, onOpenChange }: InviteUserDialogProps) {
  const inviteUser = useInviteTenantUser()
  const [success, setSuccess] = useState(false)

  const { data: myTenants = [] } = useQuery({
    queryKey: ['tenants-mine'],
    queryFn: () => tenantsApi.getMine(),
  })

  const adminTenants = myTenants.filter((t) => t.role === 'Admin')
  const isMultiTenant = adminTenants.length > 1

  const { register, handleSubmit, reset, control, setError, formState: { errors } } = useForm<FormValues>({
    defaultValues: { tenantId: defaultTenantId, email: '', role: 'Viewer' },
  })

  useEffect(() => {
    if (open) {
      reset({ tenantId: defaultTenantId, email: '', role: 'Viewer' })
      setSuccess(false)
    }
  }, [open, defaultTenantId, reset])

  const onSubmit = async (data: FormValues) => {
    try {
      await inviteUser.mutateAsync({
        tenantId: data.tenantId,
        email: data.email,
        role: data.role,
      })
      setSuccess(true)
      setTimeout(() => {
        onOpenChange(false)
      }, 2000)
    } catch (err: unknown) {
      const responseData = (err as any)?.response?.data
      const apiErrors: Array<{ field: string; errorMessage: string }> = Array.isArray(responseData)
        ? responseData
        : (responseData?.errors ?? [])

      for (const apiError of apiErrors) {
        if (apiError.field?.toLowerCase() === 'email') {
          setError('email', { message: apiError.errorMessage })
        }
      }
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-2xl">
        <DialogHeader>
          <DialogTitle>Add New User</DialogTitle>
        </DialogHeader>

        {success ? (
          <div className="py-6 text-center">
            <p className="text-sm text-green-600 font-medium">Invitation sent successfully.</p>
          </div>
        ) : (
          <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
            <div className="space-y-2">
              <Label>
                Tenant <span className="text-red-500">*</span>
              </Label>
              <Controller
                name="tenantId"
                control={control}
                render={({ field }) => (
                  <Select
                    value={field.value}
                    onValueChange={field.onChange}
                    disabled={!isMultiTenant}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select tenant" />
                    </SelectTrigger>
                    <SelectContent>
                      {adminTenants.map((t) => (
                        <SelectItem key={t.id} value={t.id}>
                          {t.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                )}
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="email">
                  Email address <span className="text-red-500">*</span>
                </Label>
                <Input
                  id="email"
                  type="email"
                  placeholder="e.g. email@email.com"
                  {...register('email', { required: 'Email is required.' })}
                />
                {errors.email && (
                  <p className="text-sm text-destructive" role="alert">
                    {errors.email.message}
                  </p>
                )}
              </div>

              <div className="space-y-2">
                <Label>
                  Role <span className="text-red-500">*</span>
                </Label>
                <Controller
                  name="role"
                  control={control}
                  render={({ field }) => (
                    <Select
                      value={field.value}
                      onValueChange={(val) => field.onChange(val as TenantMemberRole)}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Select role" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="Viewer">Viewer</SelectItem>
                        <SelectItem value="Admin">Admin</SelectItem>
                      </SelectContent>
                    </Select>
                  )}
                />
              </div>
            </div>

            <DialogFooter>
              <Button type="button" variant="ghost" onClick={() => onOpenChange(false)}>
                Cancel
              </Button>
              <Button type="submit" disabled={inviteUser.isPending}>
                <Check className="h-4 w-4 mr-2" />
                {inviteUser.isPending ? 'Sending...' : 'Send Invite'}
              </Button>
            </DialogFooter>
          </form>
        )}
      </DialogContent>
    </Dialog>
  )
}
